/**
 * Trames pédagogiques officielles ECSR (TP-01303 · JO 28/04/2016)
 *
 * Le jury CCS attend deux trames distinctes selon le contexte de séance :
 *
 *  1) TRAME_SALLE    — Cours théorique en salle (ECSR)
 *  2) TRAME_CONDUITE — Leçon de conduite (plateau ou circulation)
 *
 * Référence : Référentiel TP ECSR + REMC catégorie A + retours jurys CCS 2R.
 */

import {
  Users,
  Target,
  MessagesSquare,
  BookOpen,
  ListChecks,
  RefreshCw,
  CheckCircle2,
  Activity,
  Eye,
  Compass,
  Layers,
  Award,
  PenSquare,
  ClipboardCheck,
} from 'lucide-react';

/* ────────────────────────────────────────────────────────────────────
 * TRAME 1 — COURS EN SALLE (ECSR)
 * 7 étapes attendues par le jury pour un cours théorique.
 * ──────────────────────────────────────────────────────────────────── */
export const TRAME_SALLE = {
  id: 'salle',
  label: 'Cours en salle',
  shortLabel: 'Salle',
  description:
    "Trame officielle d'un cours théorique ECSR (en salle, avec un groupe d'élèves).",
  shortPattern: 'Accueil → Objectif → Représentations → Cours → Vérification → Synthèse → Évaluation',
  accent: '#1d4ed8',
  steps: [
    {
      id: 'accueil',
      n: 1,
      icon: Users,
      label: 'Accueil & mise en situation',
      shortLabel: 'Accueil',
      color: '#1d4ed8',
      intent: "Présenter le thème, vérifier les pré-requis, capter l'attention.",
      description:
        "Première phase visible par le jury : posture d'accueil, présentation du thème, vérification rapide des pré-requis. C'est là que tu installes le climat de la séance.",
      examples: [
        "« Aujourd'hui nous allons travailler les distances d'arrêt. »",
        "Tour de table : qui a déjà freiné en urgence ?",
        "Rappel rapide du cours précédent",
      ],
    },
    {
      id: 'objectif',
      n: 2,
      icon: Target,
      label: "Annonce des objectifs",
      shortLabel: 'Objectifs',
      color: '#cf2031',
      intent: "Dire clairement ce que l'élève saura faire à la fin du cours.",
      description:
        "Formulation observable et mesurable. Toujours partir de « À la fin de ce cours, vous serez capable de… » suivi d'un verbe d'action.",
      examples: [
        "Connaître la formule de la distance de freinage",
        "Comprendre l'impact de la vitesse sur la cinétique du choc",
        "Identifier 3 facteurs aggravants",
        "Appliquer la règle des 2 secondes en pratique",
      ],
    },
    {
      id: 'representations',
      n: 3,
      icon: MessagesSquare,
      label: 'Recueil des représentations',
      shortLabel: 'Représentations',
      color: '#7c3aed',
      intent: "Faire émerger ce que le groupe pense déjà — leviers et freins.",
      description:
        "Phase clé pour le jury : questions OUVERTES pour faire émerger les représentations initiales. Permet d'identifier les leviers à exploiter et les freins à lever pendant le cours.",
      examples: [
        "« Selon vous, à 50 km/h, en combien de mètres on s'arrête ? »",
        "« Avez-vous déjà été surpris par une distance de freinage ? »",
        "« Qu'est-ce qui peut allonger une distance d'arrêt ? »",
      ],
    },
    {
      id: 'cours',
      n: 4,
      icon: BookOpen,
      label: 'Apports pédagogiques (cours)',
      shortLabel: 'Cours',
      color: '#0891b2',
      intent: "Développer le contenu en variant méthodes et supports.",
      description:
        "Cœur du cours. Le jury évalue la VARIÉTÉ des méthodes (expositive / interrogative / active) et des supports (tableau / vidéo / photos / maquette). Une méthode ou support par sous-thème.",
      examples: [
        "Méthode interrogative : faire trouver la formule par étapes",
        "Méthode expositive : énoncer la règle puis l'illustrer",
        "Méthode active : étude de cas par sous-groupes",
        "Supports : tableau blanc, vidéo officielle, infographie, photo accident",
      ],
    },
    {
      id: 'verification',
      n: 5,
      icon: ListChecks,
      label: 'Vérification des acquis',
      shortLabel: 'Vérification',
      color: '#ea580c',
      intent: "S'assurer que le contenu est compris AVANT la synthèse.",
      description:
        "Évaluation FORMATIVE en cours de séance. Distinct de l'évaluation finale. Permet d'ajuster si besoin avant de clôturer.",
      examples: [
        "QCM de 3 à 5 questions à main levée",
        "Questions orales reformulées « avec vos mots »",
        "Mise en situation rapide : « Qu'est-ce que vous feriez si… ? »",
      ],
    },
    {
      id: 'synthese',
      n: 6,
      icon: RefreshCw,
      label: 'Synthèse',
      shortLabel: 'Synthèse',
      color: '#16a34a',
      intent: 'Reformuler les points-clés, les erreurs et les bonnes pratiques.',
      description:
        "Cristallisation des messages-clés. Le jury attend une synthèse STRUCTURÉE en 3 blocs : ce qu'il faut retenir, les pièges fréquents, les bonnes pratiques.",
      examples: [
        "« 3 points à retenir absolument… »",
        "« Les 2 erreurs fréquentes que j'ai vues aujourd'hui… »",
        "« Voici les bonnes pratiques à appliquer dès demain… »",
      ],
    },
    {
      id: 'evaluation',
      n: 7,
      icon: CheckCircle2,
      label: 'Évaluation',
      shortLabel: 'Évaluation',
      color: '#9333ea',
      intent: "Mesurer l'atteinte des objectifs annoncés en début de cours.",
      description:
        "Évaluation SOMMATIVE qui valide ou non l'atteinte des objectifs. Pas une note — un constat factuel : acquis / en cours / non acquis. Avec retour individuel.",
      examples: [
        "QCM final 5 questions sur les objectifs annoncés",
        "Cas pratique à traiter en sous-groupes",
        "Validation orale individuelle : « Énonce-moi la règle des 2 secondes »",
      ],
    },
  ],
};

/* ────────────────────────────────────────────────────────────────────
 * TRAME 2 — LEÇON DE CONDUITE (CCS 2R · plateau / circulation)
 * 4 phases attendues par le jury, 9 sous-étapes au total.
 * ──────────────────────────────────────────────────────────────────── */
export const TRAME_CONDUITE = {
  id: 'conduite',
  label: 'Leçon de conduite (plateau / circulation)',
  shortLabel: 'Conduite',
  description:
    "Trame officielle d'une leçon individuelle (plateau ou circulation), en 4 phases.",
  shortPattern:
    'Bilan → Contrat pédagogique → Apprentissage (Cours · Démo · Guidage · Autonomie) → Bilan final',
  accent: '#cf2031',
  phases: [
    { id: 'p1', label: 'Phase 1 — Bilan', color: '#0891b2' },
    { id: 'p2', label: 'Phase 2 — Contrat pédagogique', color: '#cf2031' },
    { id: 'p3', label: 'Phase 3 — Apprentissage', color: '#16a34a' },
    { id: 'p4', label: 'Phase 4 — Bilan final', color: '#9333ea' },
  ],
  steps: [
    {
      id: 'evalStatique',
      phaseId: 'p1',
      n: 1,
      icon: ClipboardCheck,
      label: 'Évaluation statique',
      shortLabel: 'Éval. statique',
      color: '#0891b2',
      intent: 'Questionner avant de monter en moto.',
      description:
        "L'élève parle, le moniteur écoute. Permet d'évaluer la mémorisation, l'auto-perception et l'état d'esprit avant de monter en moto.",
      examples: [
        "« Comment s'est passée la dernière leçon ? »",
        "« Quelles difficultés persistent selon toi ? »",
        "« Comment te sens-tu aujourd'hui ? »",
      ],
    },
    {
      id: 'evalDynamique',
      phaseId: 'p1',
      n: 2,
      icon: Activity,
      label: 'Évaluation dynamique',
      shortLabel: 'Éval. dynamique',
      color: '#0891b2',
      intent: "Faire conduire et observer en situation.",
      description:
        "Mise en situation réelle. Observation systématique du regard, de l'allure, de la trajectoire, des priorités. Le constat factuel sert de point de départ.",
      examples: [
        "Regard : porté loin / au sol ?",
        "Allure : adaptée / trop élevée / hésitante ?",
        "Trajectoire : fluide / corrections brutales ?",
        "Priorités : respectées / oubliées ?",
      ],
    },
    {
      id: 'objectif',
      phaseId: 'p2',
      n: 3,
      icon: Target,
      label: "Annonce de l'objectif",
      shortLabel: 'Objectif',
      color: '#cf2031',
      intent: "Formuler clairement le but de la leçon.",
      description:
        "À partir du constat de la phase 1, formuler un objectif observable et mesurable. Le jury attend une phrase précise : « Aujourd'hui nous allons travailler… ».",
      examples: [
        "« Réaliser un demi-tour sans poser le pied. »",
        "« Franchir une intersection à visibilité réduite en sécurité. »",
        "« Adapter sa trajectoire dans un virage à droite. »",
      ],
    },
    {
      id: 'criteres',
      phaseId: 'p2',
      n: 4,
      icon: ListChecks,
      label: 'Critères de réussite',
      shortLabel: 'Critères',
      color: '#cf2031',
      intent: 'Définir 3 à 5 critères OBSERVABLES qui prouvent la réussite.',
      description:
        "Les critères transforment l'objectif en faits mesurables. Ils servent de fil rouge pendant la leçon et de base pour l'évaluation finale et l'auto-évaluation.",
      examples: [
        "Regard porté à la sortie du virage",
        "Utilisation du point de patinage (embrayage)",
        "Équilibre maintenu — pas de pose de pied",
        "Vitesse annoncée à voix haute avant l'entrée",
      ],
    },
    {
      id: 'cours',
      phaseId: 'p3',
      n: 5,
      icon: BookOpen,
      label: 'Cours (apports)',
      shortLabel: 'Cours',
      color: '#16a34a',
      intent: "Expliquer la technique, schématiser, faire verbaliser l'élève.",
      description:
        "Apport théorique court (5-10 min) avant la pratique. Explications + schéma au tableau + questionnement pour vérifier la compréhension.",
      examples: [
        "Schéma vu de dessus : trajectoire idéale en virage à droite",
        "Explication des forces : adhérence, transfert de masse",
        "Questionnement : « Que se passe-t-il si je freine en plein virage ? »",
      ],
    },
    {
      id: 'demonstration',
      phaseId: 'p3',
      n: 6,
      icon: Eye,
      label: 'Démonstration',
      shortLabel: 'Démo',
      color: '#16a34a',
      intent: "Montrer en moto, verbaliser à voix haute.",
      description:
        "Démo COMMENTÉE (moniteur verbalise ce qu'il fait) puis éventuellement COMMENTÉE GUIDÉE (l'élève suit en parallèle). Toujours dans cet ordre : SCHÉMA → DÉMO → ESSAIS.",
      examples: [
        "Démo commentée : moniteur réalise + verbalise via casque-radio",
        "Démo commentée guidée : élève reproduit en miroir",
        "Vidéo officielle Sécurité Routière 3 min en complément",
      ],
    },
    {
      id: 'guidage',
      phaseId: 'p3',
      n: 7,
      icon: Compass,
      label: 'Guidage dégressif',
      shortLabel: 'Guidage',
      color: '#16a34a',
      intent: 'Passer de FORT → MOYEN → FAIBLE → AUTONOMIE.',
      description:
        "Le jury évalue la CAPACITÉ DU MONITEUR à diminuer progressivement son guidage. 3 niveaux successifs : verbal fort → verbal léger → gestuel uniquement. Aucun guidage = autonomie.",
      examples: [
        "FORT : « Regarde loin, accélère doucement, redresse maintenant »",
        "MOYEN : « Pense au regard », « accélère »",
        "FAIBLE : juste « regard » au bon moment",
        "Gestuel : signe de la main pour ralentir / accélérer",
      ],
    },
    {
      id: 'autonomie',
      phaseId: 'p3',
      n: 8,
      icon: Award,
      label: 'Autonomie',
      shortLabel: 'Autonomie',
      color: '#16a34a',
      intent: "L'élève réalise seul, sans guidage. Moniteur observe.",
      description:
        "Étape clé pour le jury : ABSENCE TOTALE d'intervention pendant 3 à 5 passages consécutifs. Le moniteur observe et note. C'est la preuve de l'autonomie acquise.",
      examples: [
        "3 passages consécutifs sans intervention verbale ni gestuelle",
        "Le moniteur note silencieusement les critères case par case",
        "Chronométrage si pertinent (ex: freinage d'urgence)",
      ],
    },
    {
      id: 'bilan',
      phaseId: 'p4',
      n: 9,
      icon: PenSquare,
      label: 'Bilan final',
      shortLabel: 'Bilan',
      color: '#9333ea',
      intent: "Auto-évaluation D'ABORD, puis retour moniteur, puis suite de formation.",
      description:
        "L'ÉLÈVE PARLE EN PREMIER. Toujours. Le jury vérifie que tu donnes la parole avant de la prendre. Puis reformulation et fixation de la suite.",
      examples: [
        "Auto-éval : « Qu'as-tu réussi ? Qu'est-ce qui reste difficile ? »",
        "Retour moniteur : 2-3 réussites + 1 axe de progression précis",
        "Suite de formation : « La prochaine fois nous travaillerons… »",
        "Inscription au livret REMC : sous-compétence validée ou non",
      ],
    },
  ],
};

/** Retrouve une trame par son id */
export function getTrameById(id) {
  if (id === 'salle') return TRAME_SALLE;
  if (id === 'conduite') return TRAME_CONDUITE;
  return null;
}

/** Récupère la liste des steps d'une trame */
export function getStepsForTrame(trameId) {
  return getTrameById(trameId)?.steps || [];
}

/** Groupes par phase pour la trame conduite (utilisé en UI) */
export function getStepsByPhase(trameId) {
  const trame = getTrameById(trameId);
  if (!trame?.phases) return null;
  return trame.phases.map((phase) => ({
    ...phase,
    steps: trame.steps.filter((s) => s.phaseId === phase.id),
  }));
}

/** Compat ancienne API (utilisée dans la bannière du Teacher) */
export const CANEVAS_STEPS = TRAME_CONDUITE.steps;
